import { useCallback, useState, useEffect, useMemo } from 'react'
import { useAuthStore, useDeliveryStore, useCartStore } from '@restaurant/core'
import type { ClientUser, AuthToken as CoreAuthToken } from '@restaurant/core'
import type { IPlatformServices, AppState } from '../contracts'

// Extended AuthToken type to handle both service and store formats.
// Extends the core AuthToken so values remain assignable to authStore.login,
// while allowing the optional ISO `expiresAt` form used by some flows.
export interface AuthToken extends CoreAuthToken {
  expiresAt?: string
}

export interface SessionViewModelState {
  // Delegated auth store state
  user: ClientUser | null
  token: AuthToken | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Session-specific state
  isSessionReady: boolean
  isSessionExpired: boolean
  userDisplayName: string
  isAppInForeground: boolean
}

export interface SessionViewModelActions {
  // Authentication actions
  login: (user: ClientUser, token: AuthToken) => void
  logout: () => void
  updateProfile: (updates: Partial<ClientUser>) => void

  // Session management
  markSessionReady: () => void
  checkSessionExpiry: () => void
  handleSessionTimeout: () => void

  // Loading and error management
  setLoading: (loading: boolean) => void
  clearError: () => void
}

export interface SessionViewModel extends SessionViewModelState, SessionViewModelActions {}

/**
 * Platform-agnostic SessionViewModel factory
 * Uses dependency injection for platform-specific services
 */
export const createSessionViewModel = (services: IPlatformServices) => {
  return (): SessionViewModel => {
    // Global auth state
    const authStore = useAuthStore()
    
    // Local session state
    const [isSessionReady, setIsSessionReady] = useState(false)
    const [isAppInForeground, setIsAppInForeground] = useState(
      services.platformService.isAppInForeground()
    )

    // Initialize session on mount — hydrate once, subscribe to app state changes
    useEffect(() => {
      authStore.hydrate()
      // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate must run only once on mount; authStore in deps causes infinite loop
    }, [])

    useEffect(() => {
      // Subscribe to app state changes
      const unsubscribeAppState = services.platformService.onAppStateChange((state: AppState) => {
        const isInForeground = state === 'active'
        setIsAppInForeground(isInForeground)

        if (isInForeground) {
          // Check session when app comes to foreground
          if (authStore.isAuthenticated) {
            checkSessionExpiry()
          }
        }
      })

      return unsubscribeAppState
    }, [services.platformService])

    // Derived state: check if session is expired
    const isSessionExpired = useMemo(() => {
      if (!authStore.token || !authStore.isAuthenticated) {
        return false
      }

      const token = authStore.token as any // Type assertion to handle both formats
      
      // Handle expiresAt format (ISO string)
      if (token.expiresAt) {
        const expiryTime = new Date(token.expiresAt).getTime()
        const currentTime = Date.now()
        return currentTime >= expiryTime
      }
      
      // Handle expiresIn format (seconds) - this would need to be converted to absolute time
      // For now, return false as we can't determine expiry without more context
      return false
    }, [authStore.token, authStore.isAuthenticated])

    // Derived state: user display name
    const userDisplayName = useMemo(() => {
      if (!authStore.user) {
        return 'Guest'
      }

      const { firstName, lastName, email } = authStore.user
      
      if (firstName || lastName) {
        return `${firstName} ${lastName}`.trim()
      }

      return email || 'Guest'
    }, [authStore.user])

    // Session management actions
    const markSessionReady = useCallback(() => {
      setIsSessionReady(true)
    }, [])

    const checkSessionExpiry = useCallback(() => {
      if (isSessionExpired && authStore.isAuthenticated) {
        handleSessionTimeout()
      }
    }, [isSessionExpired, authStore.isAuthenticated])

    const handleSessionTimeout = useCallback(() => {
      authStore.logout()
      useDeliveryStore.getState().clearAddresses()
      useCartStore.getState().clearCart()
      setIsSessionReady(false)

      // Platform-specific session timeout handling
      services.platformService.handleSessionTimeout()

      // Navigate to login screen
      services.navigationService.navigateToLogin()
    }, [authStore, services])

    // Authentication actions
    const login = useCallback((user: ClientUser, token: AuthToken) => {
      authStore.login(user, token)
      markSessionReady()
    }, [authStore, markSessionReady])

    const logout = useCallback(() => {
      authStore.logout()
      useDeliveryStore.getState().clearAddresses()
      useCartStore.getState().clearCart()
      setIsSessionReady(false)

      // Navigate to login screen
      services.navigationService.navigateToLogin()
    }, [authStore, services])

    const updateProfile = useCallback((updates: Partial<ClientUser>) => {
      authStore.updateProfile(updates)
    }, [authStore])

    // Loading and error management
    const setLoading = useCallback((loading: boolean) => {
      authStore.setLoading(loading)
    }, [authStore])

    const clearError = useCallback(() => {
      authStore.clearError()
    }, [authStore])

    return {
      // Delegated auth store state
      user: authStore.user,
      token: authStore.token,
      isAuthenticated: authStore.isAuthenticated,
      isLoading: authStore.isLoading,
      error: authStore.error,

      // Session-specific state
      isSessionReady,
      isSessionExpired,
      userDisplayName,
      isAppInForeground,

      // Actions
      login,
      logout,
      updateProfile,
      markSessionReady,
      checkSessionExpiry,
      handleSessionTimeout,
      setLoading,
      clearError,
    }
  }
}